import React, { useMemo, useState, useEffect } from "react";
import { Decimal } from "decimal.js";
import { Skeleton } from "@/components/ui/skeleton";
import { Commodities } from "@/lib/api/commodity";
import { useListRiskSummaryFull } from "@/lib/api/risk/summary";
import { get_customer_commodity_top_level, get_commodity_color, get_commodity_border_color, formatPercent, groupable_display_name_to_commodity_list, groupable_display_name_to_drillable_commodity_list, generateDisplayLabelForOtcDetailedType } from "./common";
import { ResponsivePie } from "@nivo/pie";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useQueryParam } from "@/hooks/use-query-param";
import { createColumnHelper, VisibilityState } from "@tanstack/react-table";
import { formatNumberToDisplay } from "@/lib/api/common";
import { DataTable } from "@/components/ui/virtualizedTable";
import { ContractTypeOptions } from "@/lib/api/otc";
import { getPath } from "@/lib/prelude";
import { ColumnVisibilityDialog } from "@/components/ui/ColumnVisibilityDialog";
import { Button } from "@/components/ui/button";
import { PreviewDisplayCard } from "./PreviewDisplayCard";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";

interface CustomerCommodityBreakdownRow {
    customer_id: string;
    commodity_type: string;
    commodity_display_name: string;

    acreage: number;
    projected_production: number;

    percent_hedged: number;
    hedged_without_beacon_quantity: number;
    hedged_without_beacon_percent: number;

    open_brokerage_quantity: number;
    open_brokerage_percent: number;

    closed_brokerage_quantity: number;
    closed_brokerage_percent: number;

    cash_quantity: number;
    cash_percent: number;

    hta_quantity: number;
    hta_percent: number;

    basis_quantity: number;
    basis_percent: number;

    beacon_quantity: number;
    beacon_percent: number;

    unsold_quantity: number;
    unsold_percent: number;

    offer_up_quantity: number;
    offer_up_percent: number;
}

export function CustomerCommodityView({ baseTopLevelResult, customerId, commodityDisplayName }: { baseTopLevelResult: ReturnType<typeof useListRiskSummaryFull>, customerId: string, commodityDisplayName: string }) {
    const summaryData = baseTopLevelResult?.data?.data?.summary || {};
    const [_commodityDisplayName, setSelectedCommodityDisplayName] = useQueryParam("commodity", "");
    const [columnVisibilityTrigger, setColumnVisibilityTrigger] = useState<React.ReactNode>(null);

    const { isReady, customerName, commodity_top_level_items } = useMemo(() => {
        if (!summaryData?.commodity_list) return { isReady: false, customerName: "Loading", commodity_top_level_items: [] };
        if (!summaryData?.customer_name_map) return { isReady: false, customerName: "Loading", commodity_top_level_items: [] };

        const customerName = customerId ? summaryData.customer_name_map[customerId] || "Unknown Customer" : "";

        const commodity_top_level_items = summaryData.commodity_list.map((commodity_type: Commodities) => get_customer_commodity_top_level({ commodity_type, customer_id: customerId, summaryData })).filter(Boolean).sort((a: any, b: any) => a.commodity_display_name.localeCompare(b.commodity_display_name));

        return { isReady: true, customerName, commodity_top_level_items };
    }, [summaryData]);

    const formatNumber = (num: number) => {
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(num);
    };

    return (
        <div className="flex flex-col gap-1">
            <h2 className="text-xl font-bold">{isReady ? null : (
                <Skeleton className="h-4 w-[80px]" />
            )}</h2>
            {!isReady && (
                <div className="flex flex-col space-y-2">
                    <Skeleton className="h-4 w-[250px]" />
                    <Skeleton className="h-4 w-[200px]" />
                </div>
            )}
            {isReady && (
                <div className="grid gap-4">
                    <Tabs value={commodityDisplayName} onValueChange={(tab) => setSelectedCommodityDisplayName(tab)} >
                        <div className="flex flex-row justify-between mb-4">
                            <TabsList>
                                <TabsTrigger value="Corn">Corn</TabsTrigger>
                                <TabsTrigger value="Soybeans">Soybeans</TabsTrigger>
                                <TabsTrigger value="Wheat">Wheat</TabsTrigger>
                                <TabsTrigger value="Canola">Canola</TabsTrigger>
                            </TabsList>
                            <div>
                                {!!columnVisibilityTrigger && (columnVisibilityTrigger)}
                            </div>
                        </div>

                        <TabsContent value="Corn">
                            <CustomerCommodityViewCards
                                customerId={customerId}
                                commodityDisplayName={commodityDisplayName}
                                baseTopLevelResult={baseTopLevelResult}
                                onColumnVisibilityTriggerChange={setColumnVisibilityTrigger}
                            />
                        </TabsContent>
                        <TabsContent value="Soybeans">
                            <CustomerCommodityViewCards
                                customerId={customerId}
                                commodityDisplayName={commodityDisplayName}
                                baseTopLevelResult={baseTopLevelResult}
                                onColumnVisibilityTriggerChange={setColumnVisibilityTrigger}
                            />
                        </TabsContent>
                        <TabsContent value="Wheat">
                            <CustomerCommodityViewCards
                                customerId={customerId}
                                commodityDisplayName={commodityDisplayName}
                                baseTopLevelResult={baseTopLevelResult}
                                onColumnVisibilityTriggerChange={setColumnVisibilityTrigger}
                            />
                        </TabsContent>
                        <TabsContent value="Canola">
                            <CustomerCommodityViewCards
                                customerId={customerId}
                                commodityDisplayName={commodityDisplayName}
                                baseTopLevelResult={baseTopLevelResult}
                                onColumnVisibilityTriggerChange={setColumnVisibilityTrigger}
                            />
                        </TabsContent>
                    </Tabs>
                </div>
            )}
        </div>
    );
}

export function CustomerCommodityViewBreakdown({ baseTopLevelResult, customerId, commodityDisplayName, onColumnVisibilityTriggerChange }: { baseTopLevelResult: ReturnType<typeof useListRiskSummaryFull>, customerId: string, commodityDisplayName: string, onColumnVisibilityTriggerChange: (trigger: React.ReactNode) => void }) {
    const summaryData = baseTopLevelResult?.data?.data?.summary || {};
    const storageKey = `customerCommodityView-columnVisibility`;

    // Column visibility state
    const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});

    // Load column visibility from localStorage on mount
    useEffect(() => {
        const savedVisibility = localStorage.getItem(storageKey);
        if (savedVisibility) {
            try {
                setColumnVisibility(JSON.parse(savedVisibility));
            } catch (error) {
                console.error('Failed to parse column visibility from localStorage:', error);
            }
        }
    }, [storageKey]);

    const { isReady, rows, columns, columnList } = useMemo(() => {
        if (!summaryData?.commodity_list) return { isReady: false, commodity_top_level_items: [], rows: [], columns: [], columnList: [] };
        if (!summaryData?.customer_name_map) return { isReady: false, commodity_top_level_items: [], rows: [], columns: [], columnList: [] };

        const otc_types = summaryData.otc_types as string[];

        const rows: any[] = summaryData.commodity_list.map((commodity_type: Commodities) => {
            const item = get_customer_commodity_top_level({ commodity_type, customer_id: customerId, summaryData });
            if (!item) return null;
            return {
                ...item,
            };
        }).filter(Boolean);

        const columnHelper = createColumnHelper<CustomerCommodityBreakdownRow>();
        const detailed_otc_columns_breakdown = otc_types.map((otc_type: string) => {
            const column_id = `otc_${otc_type}` as any;
            return columnHelper.accessor(column_id, {
                id: column_id,
                header: generateDisplayLabelForOtcDetailedType(otc_type),
                ...({
                    accessorFn: (row: any) => {
                        return row.otc_full_map_stats[otc_type]?.quantity || 0;
                    }
                }),
                cell: (info) => {
                    return <span className="text-gray-600 text-sm">{formatNumberToDisplay(info.getValue(), { emptyFormat: '--', zeroFormat: '--' })}</span>;
                },
            });
        });
        const base_known_columns = [
            columnHelper.accessor("acreage", {
                id: "acreage",
                header: "Acreage",
                filterFn: "numberFilter" as any,
                cell: (info) => {
                    return <span className="text-gray-600 text-sm">{formatNumberToDisplay(info.getValue())}</span>;
                },
            }),
            columnHelper.accessor("projected_production", {
                id: "projected_production",
                header: "Projected Production",
                filterFn: "numberFilter" as any,
                cell: (info) => {
                    return <span className="text-gray-600 text-sm">{formatNumberToDisplay(info.getValue())}</span>;
                },
            }),
            columnHelper.accessor("percent_hedged", {
                id: "percent_hedged",
                header: "Percent Hedged",
                filterFn: "percentageFilter" as any,
                cell: (info) => {
                    return <span className="text-gray-600 text-sm">{formatNumberToDisplay(info.getValue(), { emptyFormat: '0 %', zeroFormat: '0 %', suffix: "%" })}</span>;
                },
            }),
            columnHelper.accessor("open_brokerage_quantity", {
                id: "open_brokerage_quantity",
                header: "Brokerage",
                filterFn: "numberFilter" as any,
                cell: (info) => {
                    return <span className="text-gray-600 text-sm">{formatNumberToDisplay(info.getValue(), { emptyFormat: '--', zeroFormat: '--' })}</span>;
                },
            }),
            columnHelper.accessor("cash_quantity", {
                id: "cash_quantity",
                header: "Cash",
                filterFn: "numberFilter" as any,
                cell: (info) => {
                    return <span className="text-gray-600 text-sm">{formatNumberToDisplay(info.getValue(), { emptyFormat: '--', zeroFormat: '--' })}</span>;
                },
            }),
            columnHelper.accessor("hta_quantity", {
                id: "hta_quantity",
                header: "HTA",
                filterFn: "numberFilter" as any,
                cell: (info) => {
                    return <span className="text-gray-600 text-sm">{formatNumberToDisplay(info.getValue(), { emptyFormat: '--', zeroFormat: '--' })}</span>;
                },
            }),
            columnHelper.accessor("basis_quantity", {
                id: "basis_quantity",
                header: "Basis",
                filterFn: "numberFilter" as any,
                cell: (info) => {
                    return <span className="text-gray-600 text-sm">{formatNumberToDisplay(info.getValue(), { emptyFormat: '--', zeroFormat: '--' })}</span>;
                },
            }),
            columnHelper.accessor("beacon_quantity", {
                id: "beacon_quantity",
                header: "Beacon",
                filterFn: "numberFilter" as any,
                cell: (info) => {
                    return <span className="text-gray-600 text-sm">{formatNumberToDisplay(info.getValue(), { emptyFormat: '--', zeroFormat: '--' })}</span>;
                },
            }),
            columnHelper.accessor("unsold_quantity", {
                id: "unsold_quantity",
                header: "Unsold",
                filterFn: "numberFilter" as any,
                cell: (info) => {
                    return <span className="text-gray-600 text-sm">{formatNumberToDisplay(info.getValue(), { emptyFormat: '--', zeroFormat: '--' })}</span>;
                },
            }),
        ];
        const columns = [...base_known_columns, ...detailed_otc_columns_breakdown];

        // Create a simplified column list for the visibility dialog
        const columnList = columns.map(col => ({
            id: col.id || '',
            header: typeof col.header === 'string' ? col.header : (col.id || 'Column')
        }));

        return { isReady: true, rows, columns, columnList };
    }, [summaryData]);

    // Pass the column visibility trigger to the parent when component is ready
    useEffect(() => {
        if (isReady) {
            onColumnVisibilityTriggerChange((
                <ColumnVisibilityDialog
                    columns={columnList}
                    visibilityState={columnVisibility}
                    onVisibilityChange={setColumnVisibility}
                    storageKey={storageKey}
                    trigger={
                        <Button variant="outline" size="sm">
                            Manage Columns
                        </Button>
                    }
                />
            ));
        } else {
            onColumnVisibilityTriggerChange(null);
        }
    }, [isReady, columnList, columnVisibility, storageKey, onColumnVisibilityTriggerChange]);

    return (
        <div>
            {isReady && (
                <div className="grid gap-4">
                    <DataTable
                        defaultSorting={[]}
                        height="800px"
                        columns={columns as any}
                        data={rows!}
                        className={`mx-4`}
                        columnVisibility={columnVisibility}
                        onColumnVisibilityChange={setColumnVisibility}
                    />
                </div>
            )}
        </div>
    );
}

export function CustomerCommodityViewCards({ baseTopLevelResult, customerId, commodityDisplayName, onColumnVisibilityTriggerChange }: { baseTopLevelResult: ReturnType<typeof useListRiskSummaryFull>, customerId: string, commodityDisplayName: string, onColumnVisibilityTriggerChange: (trigger: React.ReactNode) => void }) {
    const summaryData = baseTopLevelResult?.data?.data?.summary || {};
    const [_detailView, setDetailView] = useQueryParam("detail_view", "");

    const { isReady, commodityData } = useMemo(() => {
        if (!summaryData?.commodity_list) return { isReady: false, commodityData: null };
        if (!summaryData?.customer_name_map) return { isReady: false, commodityData: null };

        const groupable_commodity_type = groupable_display_name_to_drillable_commodity_list(commodityDisplayName);

        const item = get_customer_commodity_top_level({
            commodity_type: groupable_commodity_type[0],
            customer_id: customerId,
            summaryData
        });

        return { isReady: true, commodityData: item };
    }, [summaryData, customerId, commodityDisplayName]);

    // Clear the column visibility trigger since we don't need it for cards
    useEffect(() => {
        onColumnVisibilityTriggerChange(null);
    }, [onColumnVisibilityTriggerChange]);

    if (!isReady) {
        return (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {[...Array(6)].map((_, i) => (
                    <Skeleton key={i} className="h-32 w-full" />
                ))}
            </div>
        );
    }

    if (!commodityData) {
        return (
            <div className="text-center py-8 text-gray-500">
                No data available for {commodityDisplayName}
            </div>
        );
    }

    const formatQuantity = (value: any) => formatNumberToDisplay(value, { emptyFormat: '--', zeroFormat: '--' });
    const formatPercent = (value: any) => formatNumberToDisplay(value, { emptyFormat: '0 %', zeroFormat: '0 %', suffix: "%" });

    return (
        <div className="space-y-6">
            {/* Production Overview */}
            <div className="grid gap-4 md:grid-cols-2">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-blue-900 mb-4">Production Overview</h3>
                    <div className="space-y-3">
                        <div className="flex justify-between items-center">
                            <span className="text-blue-700 font-medium">Acreage</span>
                            <span className="text-blue-900 font-bold text-lg">{formatQuantity(commodityData.acreage)}</span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-blue-700 font-medium">Production</span>
                            <span className="text-blue-900 font-bold text-lg">{formatQuantity(commodityData.production)}</span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-blue-700 font-medium">Physical Risk</span>
                            <span className="text-blue-900 font-bold text-lg">{formatQuantity(commodityData.percent_physical_quantity)} ({formatPercent(commodityData.percent_physical)})</span>
                        </div>
                        <div className="flex border-t pt-2 justify-between items-center">
                            <span className="text-blue-700 font-medium col-span-2">Unsold</span>
                            <span className="text-blue-900 font-bold text-lg text-center col-span-1">
                                {formatQuantity(commodityData.unprotected_physical)} ({formatPercent(commodityData.unprotected_physical_percent)})
                            </span>
                        </div>
                    </div>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-green-900 mb-4">Hedging Summary</h3>
                    <div className="flex flex-col gap-4">
                        <div className="grid grid-cols-3 gap-2 items-center">
                            <span className="text-green-700 font-medium"></span>
                            <span className="text-green-700 font-medium text-sm text-center">Normal</span>
                            <span className="text-green-700 font-medium text-sm text-center">With Offer</span>

                            <span className="text-green-700 font-medium">Total Hedged</span>
                            <span className="text-green-900 font-bold text-lg text-center">{formatPercent(commodityData.percentOverProjectedProduction(commodityData.percentHedgedWithBeaconBreakdowns.find(item => item.id === 'base')?.value || 0))}</span>
                            <span className="text-green-900 font-bold text-lg text-center">{formatPercent(commodityData.percentOverProjectedProduction(commodityData.percentHedgedWithBeaconBreakdowns.find(item => item.id === 'base_with_open_offer')?.value || 0))}</span>
                        </div>
                        <div className="grid grid-cols-3 border-t pt-3 mt-2 items-center">
                            <span className="text-green-700 font-medium col-span-2">Unprotected</span>
                            <span className="text-green-900 font-bold text-lg text-right col-span-1">
                                {formatQuantity(commodityData.unprotected_futures)} ({formatPercent(commodityData.unprotected_futures_percent)})
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex flex-col md:grid md:grid-cols-3 gap-4">
                <div className="bg-white border border-gray-200 rounded-lg p-6 col-span-2">
                    <h3 className="text-lg font-semibold text-gray-900 mb-6">Physical Risk</h3>
                    <div className="grid gap-4 md:grid-cols-2 2xl:grid-cols-4">
                        <PreviewDisplayCard detailViewToRender="cash" commodityData={commodityData} enableClick={true} />
                        <PreviewDisplayCard detailViewToRender="basis" commodityData={commodityData} enableClick={true} />
                        <PreviewDisplayCard detailViewToRender="otc-beacon-basis" commodityData={commodityData} enableClick={true} nameMetaAppendix="| Basis" />
                        <PreviewDisplayCard detailViewToRender="dpq-basis" commodityData={commodityData} enableClick={true} nameMetaAppendix="| Basis" />
                    </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-6">DPQ</h3>
                    <div className="grid gap-4 md:grid-cols-1">
                        <PreviewDisplayCard detailViewToRender="non_applicable_dp" commodityData={commodityData} enableClick={true} />
                    </div>
                </div>
            </div>


            <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">Futures Risk</h3>
                <div className="grid gap-4 md:grid-cols-2 md:grid-cols-3 2xl:grid-cols-5">
                    <PreviewDisplayCard detailViewToRender="open_brokerage" commodityData={commodityData} enableClick={true} nameMetaAppendix={(() => {
                        const detailedCustomer = baseTopLevelResult?.data?.data?.summary?.detailed_customer_info || {};
                        const nameMetaAppendix: string | undefined = detailedCustomer?.active_account_codes?.[0] ?? "";
                        return nameMetaAppendix ? `| ${nameMetaAppendix}` : "";
                    })()} />
                    <PreviewDisplayCard detailViewToRender="hta" commodityData={commodityData} enableClick={true} />
                    {/* Beacon has implied futures */}
                    {/* <PreviewDisplayCard detailViewToRender="beacon" commodityData={commodityData} enableClick={true} /> */}
                    <PreviewDisplayCard detailViewToRender="otc-futures" commodityData={commodityData} enableClick={true} nameMetaAppendix="| Futures" />
                    <PreviewDisplayCard detailViewToRender="dpq-futures" commodityData={commodityData} enableClick={true} nameMetaAppendix="| Futures" />
                </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-6 grid grid-cols-[auto,auto,2fr,2fr] gap-4 space-y-2">
                <span className={cn(commodityData.open_offer_stats !== 0 ? "flex gap-4" : "grid grid-cols-1 gap-2")}>
                    {commodityData.open_offer_stats === 0 && (
                        <div className="flex flex-col col-span-1">
                            <h3 className="text-lg font-semibold text-gray-900 mb-6">No Open Offers</h3>
                        </div>
                    )}
                {commodityData.open_offer_stats > 0 && (
                    <div className="flex flex-col col-span-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-6">Open Offers</h3>
                        <div className="grid gap-4 min-w-[225px]">
                            <PreviewDisplayCard detailViewToRender="open_offers" commodityData={commodityData} enableClick={true} />
                        </div>
                    </div>
                )}
                {commodityData.base_open_offer_stats > 0 && (
                    <div className="flex flex-col col-span-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-6">&nbsp;</h3>
                        <div className="grid gap-4 min-w-[220px]">
                            <PreviewDisplayCard detailViewToRender="base_open_offers" commodityData={commodityData} enableClick={true} />
                        </div>
                    </div>
                )}
                {commodityData.manual_open_offer_stats > 0 && (
                    <div className="flex flex-col col-span-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-6">&nbsp;</h3>
                        <div className="grid gap-4 min-w-[220px]">
                            <PreviewDisplayCard detailViewToRender="manual_open_offers" commodityData={commodityData} enableClick={true} />
                        </div>
                    </div>
                )}
                {commodityData.manual_otc_offer_up_stats > 0 && (
                    <div className="flex flex-col col-span-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-6">&nbsp;</h3>
                        <div className="grid gap-4 min-w-[220px]">
                            <PreviewDisplayCard detailViewToRender="manual_otc_offer_ups" commodityData={commodityData} enableClick={true} />
                        </div>
                    </div>
                )}
                </span>
                {commodityData.open_offer_stats !== 0 && (<Separator orientation="vertical" />)}
                {
                    commodityData.double_up_map_stats && Object.keys(commodityData.double_up_map_stats).length > 0 && (
                        <div className="flex flex-col col-span-2">
                            <h3 className="text-lg font-semibold text-gray-900 mb-6">OTC Contracts Breakdown</h3>
                            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                                <PreviewDisplayCard detailViewToRender="otc" commodityData={commodityData} enableClick={true} nameMetaAppendix="| Total" />
                                {Object.entries(commodityData.double_up_map_stats).map(([otcType, stats]: [string, any]) => (
                                    <PreviewDisplayCard detailViewToRender={`otc_${otcType}`} commodityData={commodityData} otcInfo={[otcType, stats]} enableClick={true} key={`otc_${customerId}_${commodityDisplayName}_${otcType}`} />
                                ))}
                            </div>
                        </div>
                    )
                }
            </div>

            <div className="flex flex-col md:grid md:grid-cols-3 gap-4">
                <div className="bg-white border border-gray-200 rounded-lg p-6 col-span-3">
                    <h3 className="text-lg font-semibold text-gray-900 mb-6">Direct Sale</h3>
                    <div className="grid gap-4 md:grid-cols-2 2xl:grid-cols-4">
                        <PreviewDisplayCard detailViewToRender="direct-sales-non-appl" commodityData={commodityData} enableClick={true} />
                        <PreviewDisplayCard detailViewToRender="direct-sales-futures" commodityData={commodityData} enableClick={true} nameMetaAppendix="| Futures" />
                        <PreviewDisplayCard detailViewToRender="direct-sales-basis" commodityData={commodityData} enableClick={true} nameMetaAppendix="| Basis" />
                    </div>
                </div>
            </div>
        </div >
    );
}
